import {
  STATUS_EMISSAO_NFCE_PENDENTE,
  STATUS_EMISSAO_NFCE_PROCESSANDO,
  STATUS_EMISSAO_NFCE_INDEFINIDA,
  STATUS_EMISSAO_NFCE_CONTINGENCIA,
  STATUS_EMISSAO_NFCE_CONTINGENCIA_ENVIADA,
  STATUS_EMISSAO_NFCE_DESCONHECIDO,
  STATUS_EMISSAO_NFCE_AUTORIZADA,
  STATUS_EMISSAO_NFCE_AUTORIZADA_SEM_XML,
  STATUS_EMISSAO_NFCE_REJEITADA,
  STATUS_EMISSAO_NFCE_REJEITADA_DEFINITIVO,
  STATUS_EMISSAO_NFCE_CANCELADA,
  STATUS_DEVOLUCAO_NFCE_PENDENTE,
  STATUS_DEVOLUCAO_NFCE_PROCESSANDO,
  STATUS_DEVOLUCAO_NFCE_AUTORIZADA,
  STATUS_DEVOLUCAO_NFCE_REJEITADA,
  STATUS_DEVOLUCAO_NFCE_INDEFINIDA,
  STATUS_DEVOLUCAO_NFCE_FALHA_DEFINITIVA,
  PRAZO_MIN_CONVERSAO_NFCE_MINUTOS,
  PRAZO_MAX_CONVERSAO_NFCE_DIAS,
} from './constants';

/*
 * Espelho da máquina de decisão do backend
 * (server/app/Utils/fiscal/cancelamento/decidirAcaoFiscal.js).
 *
 * Serve só para a UX — decidir qual diálogo abrir e cortar cedo os casos que o
 * servidor recusaria de qualquer jeito. A fonte de verdade continua sendo o
 * backend, que revalida tudo.
 */

export const TIPO_NOTA_FISCAL_NFE = 'nfe';
export const TIPO_NOTA_FISCAL_NFCE = 'nfce';

export const JUSTIFICATIVA_TAMANHO_MINIMO = 15;
export const JUSTIFICATIVA_TAMANHO_MAXIMO = 255;

export const PRAZO_CANCELAMENTO_NFCE_EM_MINUTOS = 30;
export const PRAZO_CANCELAMENTO_NFE_EM_MINUTOS = 24 * 60;

export const ACAO_FISCAL_SEGUIR = 'seguir';
export const ACAO_FISCAL_CANCELAR = 'cancelar';
export const ACAO_FISCAL_INUTILIZAR = 'inutilizar';
export const ACAO_FISCAL_BLOQUEAR = 'bloquear';

const STATUS_EM_PROCESSAMENTO = [
  STATUS_EMISSAO_NFCE_PENDENTE,
  STATUS_EMISSAO_NFCE_PROCESSANDO,
  STATUS_EMISSAO_NFCE_INDEFINIDA,
  STATUS_EMISSAO_NFCE_CONTINGENCIA,
  STATUS_EMISSAO_NFCE_CONTINGENCIA_ENVIADA,
  STATUS_EMISSAO_NFCE_DESCONHECIDO,
];

const STATUS_COM_DOCUMENTO_AUTORIZADO = [
  STATUS_EMISSAO_NFCE_AUTORIZADA,
  STATUS_EMISSAO_NFCE_AUTORIZADA_SEM_XML,
];

const STATUS_COM_NUMERACAO_QUEIMADA = [
  STATUS_EMISSAO_NFCE_REJEITADA,
  STATUS_EMISSAO_NFCE_REJEITADA_DEFINITIVO,
];

/** `tipoNota` nulo é NFC-e — convenção histórica, ver o backend. */
export function ehNfe(tipoNota) {
  return tipoNota === TIPO_NOTA_FISCAL_NFE;
}

export function rotuloDaNota(tipoNota) {
  return ehNfe(tipoNota) ? 'NF-e' : 'NFC-e';
}

export function prazoDeCancelamentoEmMinutos(tipoNota) {
  return ehNfe(tipoNota)
    ? PRAZO_CANCELAMENTO_NFE_EM_MINUTOS
    : PRAZO_CANCELAMENTO_NFCE_EM_MINUTOS;
}

export function formatarDuracao(minutos) {
  if (minutos < 60) return `${minutos} min`;

  const horas = Math.floor(minutos / 60);
  const restante = minutos % 60;

  return restante === 0 ? `${horas} h` : `${horas} h ${restante} min`;
}

export function minutosRestantesDoPrazo(venda) {
  const referencia = venda.data_autorizacao || venda.data_emissao;
  if (!referencia) return null;

  const decorridos = Math.floor(
    (Date.now() - new Date(referencia).getTime()) / 60000,
  );

  return prazoDeCancelamentoEmMinutos(venda.tipoNota) - decorridos;
}

export function decidirAcaoFiscal(venda) {
  const status = venda.status_emissao_nfce;

  // Conversão NFC-e -> NF-e em andamento (ou parada): o servidor bloqueia o
  // cancelamento até ela terminar.
  if (venda.devolucao_nfce) return ACAO_FISCAL_BLOQUEAR;

  if (!status) return ACAO_FISCAL_SEGUIR;
  if (STATUS_EM_PROCESSAMENTO.includes(status)) return ACAO_FISCAL_BLOQUEAR;
  if (STATUS_COM_DOCUMENTO_AUTORIZADO.includes(status)) {
    return ACAO_FISCAL_CANCELAR;
  }
  if (STATUS_COM_NUMERACAO_QUEIMADA.includes(status)) {
    // Rejeição antes de a numeração ser alocada: não há faixa a inutilizar.
    return venda.numero_nfce && venda.serie_nfce
      ? ACAO_FISCAL_INUTILIZAR
      : ACAO_FISCAL_SEGUIR;
  }
  return ACAO_FISCAL_SEGUIR;
}

export function reativacaoBloqueada(venda) {
  return venda.status_emissao_nfce === STATUS_EMISSAO_NFCE_CANCELADA;
}

export const MENSAGEM_REATIVACAO_BLOQUEADA =
  'A nota fiscal desta venda foi cancelada na SEFAZ e não pode ser reativada. Faça uma nova venda.';

export function mensagemNotaEmProcessamento(venda) {
  if (venda.devolucao_nfce) {
    return 'Esta venda está em conversão de NFC-e para NF-e. Aguarde a conclusão (ou trate a conversão na lista de NFC-e) antes de cancelar.';
  }

  return `A nota fiscal desta venda ainda está em processamento (situação: ${venda.status_emissao_nfce}). Aguarde a conclusão e tente novamente.`;
}

/*
 * Conversão de NFC-e autorizada em NF-e (via NF-e de devolução).
 *
 * Espelho de UX das validações do backend (VendaController.converterNfceParaNfe).
 * A fonte de verdade é o servidor, que revalida tudo — isto só decide se o botão
 * aparece na lista de NFC-e.
 */

/** Mesmo mês civil (ano + mês) — regra de negócio da janela de conversão. */
function mesmoMesCivil(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

export function podeConverterNfceParaNfe(r, agora = Date.now()) {
  if (!r || r.cancelado) return false;
  if (ehNfe(r.tipoNota)) return false; // já é NF-e (tipoNota nulo = NFC-e)
  if (r.devolucao_nfce) return false; // conversão já em andamento
  if (r.status_emissao_nfce !== STATUS_EMISSAO_NFCE_AUTORIZADA) return false;
  if (!r.data_autorizacao) return false;

  const dataAutorizacao = new Date(r.data_autorizacao);
  const minutos = (agora - dataAutorizacao.getTime()) / 60000;

  return (
    minutos >= PRAZO_MIN_CONVERSAO_NFCE_MINUTOS &&
    minutos <= PRAZO_MAX_CONVERSAO_NFCE_DIAS * 1440 &&
    mesmoMesCivil(dataAutorizacao, new Date(agora))
  );
}

/**
 * Devolução rejeitada pela SEFAZ: `devolucao_nfce` continua 1 (a conversão
 * "está em andamento" no banco), mas o worker não vai reprocessar sozinho — o
 * servidor aceita reentrada em `converterNfceParaNfe` só quando
 * `status_devolucao_nfce === 'rejeitada'` (iniciarConversaoNfceParaNfe é
 * reentrante nesse caso). Mesma janela máxima de `podeConverterNfceParaNfe`
 * (o mínimo de 30 min não se aplica de novo — a NFC-e já passou desse ponto).
 */
export function podeReprocessarDevolucaoNfce(r, agora = Date.now()) {
  if (!r || r.cancelado) return false;
  if (ehNfe(r.tipoNota)) return false;
  if (!r.devolucao_nfce) return false;
  if (r.status_devolucao_nfce !== STATUS_DEVOLUCAO_NFCE_REJEITADA) return false;
  if (!r.data_autorizacao) return false;

  const dataAutorizacao = new Date(r.data_autorizacao);
  const minutos = (agora - dataAutorizacao.getTime()) / 60000;

  return (
    minutos <= PRAZO_MAX_CONVERSAO_NFCE_DIAS * 1440 &&
    mesmoMesCivil(dataAutorizacao, new Date(agora))
  );
}

/**
 * Devolução `indefinida` (transmissão sem resposta): o worker consulta a SEFAZ
 * sozinho a cada ciclo; esta ação só antecipa a consulta. Não muda o estado.
 */
export function podeConsultarDevolucaoNfce(r) {
  if (!r || r.cancelado) return false;
  if (ehNfe(r.tipoNota)) return false;
  if (!r.devolucao_nfce) return false;

  return r.status_devolucao_nfce === STATUS_DEVOLUCAO_NFCE_INDEFINIDA;
}

/**
 * Devolução em `falha_definitiva` (terminal): reiniciar zera os contadores e
 * exige a digital de um gerente. Mesma janela da conversão (até
 * PRAZO_MAX_CONVERSAO_NFCE_DIAS dias e no mesmo mês da autorização).
 */
export function podeReiniciarConversaoNfce(r, agora = Date.now()) {
  if (!r || r.cancelado) return false;
  if (ehNfe(r.tipoNota)) return false;
  if (!r.devolucao_nfce) return false;
  if (r.status_devolucao_nfce !== STATUS_DEVOLUCAO_NFCE_FALHA_DEFINITIVA) {
    return false;
  }
  if (!r.data_autorizacao) return false;

  const dataAutorizacao = new Date(r.data_autorizacao);
  const minutos = (agora - dataAutorizacao.getTime()) / 60000;

  return (
    minutos <= PRAZO_MAX_CONVERSAO_NFCE_DIAS * 1440 &&
    mesmoMesCivil(dataAutorizacao, new Date(agora))
  );
}

/** Há conversão registrada na venda (em andamento, parada ou concluída). */
export function temConversaoNfce(r) {
  return Boolean(r && r.status_devolucao_nfce);
}

const ROTULO_STATUS_DEVOLUCAO_NFCE = {
  [STATUS_DEVOLUCAO_NFCE_PENDENTE]: 'Conversão na fila',
  [STATUS_DEVOLUCAO_NFCE_PROCESSANDO]: 'Emitindo devolução',
  [STATUS_DEVOLUCAO_NFCE_AUTORIZADA]: 'Devolução autorizada',
  [STATUS_DEVOLUCAO_NFCE_REJEITADA]: 'Devolução rejeitada',
  [STATUS_DEVOLUCAO_NFCE_INDEFINIDA]: 'Devolução — verificar',
  [STATUS_DEVOLUCAO_NFCE_FALHA_DEFINITIVA]: 'Conversão parada',
};

export function rotuloStatusDevolucaoNfce(status) {
  return ROTULO_STATUS_DEVOLUCAO_NFCE[status] || 'Convertendo em NF-e';
}

export const OPCOES_JUSTIFICATIVA_CANCELAMENTO = [
  'Cancelamento a pedido do cliente. Nao houve circulacao de mercadoria.',
  'Erro de digitacao nos dados da venda. Nao houve circulacao de mercadoria.',
  'Emissao em duplicidade da nota fiscal desta venda.',
  'Erro operacional no registro dos itens da venda.',
].map((texto) => ({ label: texto, value: texto }));
